import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { PageHeader } from '../../app/PageHeader'
import copyJson from '../../demo/fixtures/copy.json'
import campaignsJson from '../../demo/fixtures/campaigns.json'
import enumsJson from '../../demo/fixtures/enums.json'
import { useFilteredCampaigns, useTabCounts } from '../../demo/selectors'
import { useDemoStore } from '../../demo/store'
import type { TabKey } from '../../demo/types'
import { Button, Card, EmptyState, Icon, Input, Tabs } from '../../ui'
import {
  AUTOPILOT_COPY,
  AUTOPILOT_TAB,
  filterAutopilotCampaigns,
  useTrackedCampaigns,
} from './autopilot'
import { CampaignTable } from './CampaignTable'
import {
  COMPLIANCE_TABS,
  complianceCounts,
  filterComplianceCampaigns,
  type MonitorTabKey,
} from './compliance'
import { KpiRow } from './KpiRow'
import styles from './index.module.css'

interface MonitorFixture {
  meta: { footnoteMonitor: string }
}

interface EnumFixture {
  tabs: Array<{ key: TabKey; label: string }>
}

const monitorFixture = campaignsJson as unknown as MonitorFixture
const enumFixture = enumsJson as unknown as EnumFixture
const validTabs = new Set<MonitorTabKey>([
  ...enumFixture.tabs.map((item) => item.key),
  ...COMPLIANCE_TABS.map((item) => item.key),
  AUTOPILOT_TAB.key,
])

function initialTab(search: string): MonitorTabKey {
  const requested = new URLSearchParams(search).get('tab') as MonitorTabKey | null
  return requested && validTabs.has(requested) ? requested : 'all'
}

export function MonitorPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const tab = initialTab(location.search)
  const [query, setQuery] = useState('')
  const counts = useTabCounts()
  const allCampaigns = useDemoStore((state) => state.campaigns)
  const violations = useDemoStore((state) => state.violations)
  const risks = useDemoStore((state) => state.risks)
  const trackedCampaigns = useTrackedCampaigns()
  const baseCampaigns = useFilteredCampaigns(tab === 'violation' || tab === 'risk' || tab === 'autopilot' ? 'all' : tab, query)
  const complianceCampaigns = filterComplianceCampaigns(baseCampaigns, tab, violations, risks)
  const campaigns = filterAutopilotCampaigns(complianceCampaigns, tab, trackedCampaigns)
  const compliance = complianceCounts(allCampaigns, violations, risks)
  const tabs = [
    ...enumFixture.tabs.flatMap((item) => {
      if (item.key !== 'completed') return [{ ...item, count: counts[item.key] }]
      return [
        ...COMPLIANCE_TABS.map((complianceTab) => ({ ...complianceTab, count: compliance[complianceTab.key] })),
        { ...item, count: counts[item.key] },
      ]
    }),
    { ...AUTOPILOT_TAB, count: trackedCampaigns.length },
  ]
  const runsByCampaign = new Map(trackedCampaigns.map(({ campaign, runs }) => [campaign.id, runs]))
  const searching = query.trim().length > 0
  const autopilotEmpty = tab === 'autopilot' && trackedCampaigns.length === 0

  return (
    <>
      <PageHeader
        title={copyJson.pages.monitor.title}
        subtitle={copyJson.pages.monitor.subtitle}
        action={<Button size="pill-sm" onClick={() => { void navigate('/create') }}>{copyJson.pages.monitor.action}</Button>}
      />
      <KpiRow />
      <div className={styles.tabbar}>
        <Tabs
          data-scope="monitor-tabs"
          items={tabs}
          value={tab}
          onChange={(next) => {
            const search = next === 'all' ? '' : `?tab=${next}`
            void navigate({ pathname: location.pathname, search })
          }}
        />
        <div
          className={styles.search}
          data-scope="monitor-search"
          onClick={(event) => event.currentTarget.querySelector('input')?.focus()}
        >
          <Input
            aria-label={copyJson.labels.searchPlaceholder}
            className={styles.searchInput}
            icon={<Icon name="search" size={17} />}
            placeholder={copyJson.labels.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>
      <Card className={styles.tableCard} data-scope="monitor-table">
        {autopilotEmpty ? (
          <div className={styles.autopilotEmpty} data-scope="monitor-ap-empty">
            <p className={styles.autopilotEmptyTitle}>{AUTOPILOT_COPY.empty.title}</p>
            <p className={styles.autopilotEmptyText}>{AUTOPILOT_COPY.empty.sub}</p>
            <p className={styles.autopilotEmptyAction}>
              <Button variant="secondary" size="pill-sm" onClick={() => { void navigate('/monitor') }}>
                {AUTOPILOT_COPY.empty.cta}
              </Button>
            </p>
          </div>
        ) : campaigns.length > 0 ? (
          <CampaignTable
            campaigns={campaigns}
            tab={tab}
            runsByCampaign={runsByCampaign}
            onCampaignClick={(id) => { void navigate(`/monitor/${id}`) }}
          />
        ) : (
          <EmptyState
            icon={searching ? 'search' : 'inbox'}
            title={searching ? copyJson.empty.search.title : copyJson.empty.tab.title}
            text={searching ? copyJson.empty.search.text : copyJson.empty.tab.text}
          />
        )}
      </Card>
      <p className={styles.footnote} data-scope="monitor-footnote">
        <Icon name="info" size={16} />
        {monitorFixture.meta.footnoteMonitor}
      </p>
    </>
  )
}
